// Chakra imports
import {
  Box,
  Button,
  Flex,
  Text,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  Badge,
  Divider,
  Icon,
  SimpleGrid,
  Card,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Select,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Switch,
} from '@chakra-ui/react';

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// Services
import usersService from 'services/usersService';
import { formatDate } from 'utils/format';

// Icons
import { MdArrowBack, MdPerson, MdEmail, MdLocationOn, MdAccountCircle, MdEdit, MdDelete, MdCalendarToday, MdAccessTime } from 'react-icons/md';

export default function ViewUser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  // Chakra Color Mode - All hooks must be at the top
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textColorSecondary = useColorModeValue('secondaryGray.600', 'white');
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('secondaryGray.200', 'whiteAlpha.100');
  const bgStatus = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');
  const inputBg = useColorModeValue('secondaryGray.300', 'navy.900');

  // State
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    center: '',
    role: '',
    is_active: true,
  });
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activating, setActivating] = useState(false);
  
  // Modals
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const cancelRef = useRef();

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      if (!id) {
        setError('User ID is required');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await usersService.getUser(id);

        if (result.success && result.data) {
          const userData = result.data;
          setUser(userData);
          setFormData({
            name: userData.name || '',
            email: userData.email || '',
            center: userData.center || '',
            role: userData.role || '',
            is_active: userData.is_active !== undefined ? userData.is_active : true,
          });
        } else {
          setError(result.error || 'Failed to load user');
        }
      } catch (err) {
        console.error('Error fetching user:', err);
        setError('Failed to load user. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [id]);

  // Handle update user
  const handleUpdate = async () => {
    setUpdating(true);
    try {
      const result = await usersService.updateUserPartial(id, formData);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'User updated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onEditClose();
        // Refresh user data
        const fetchResult = await usersService.getUser(id);
        if (fetchResult.success && fetchResult.data) {
          setUser(fetchResult.data);
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to update user',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Update error:', err);
      toast({
        title: 'Error',
        description: 'Failed to update user. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  // Handle activate user
  const handleActivate = async () => {
    setActivating(true);
    try {
      const result = await usersService.activateUser(id);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'User activated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // Refresh user data
        const fetchResult = await usersService.getUser(id);
        if (fetchResult.success && fetchResult.data) {
          setUser(fetchResult.data);
          setFormData(prev => ({ ...prev, is_active: true }));
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to activate user',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Activate error:', err);
      toast({
        title: 'Error',
        description: 'Failed to activate user. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActivating(false);
    }
  };

  // Handle deactivate user
  const handleDeactivate = async () => {
    setActivating(true);
    try {
      const result = await usersService.deactivateUser(id);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'User deactivated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        // Refresh user data
        const fetchResult = await usersService.getUser(id);
        if (fetchResult.success && fetchResult.data) {
          setUser(fetchResult.data);
          setFormData(prev => ({ ...prev, is_active: false }));
        }
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to deactivate user',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Deactivate error:', err);
      toast({
        title: 'Error',
        description: 'Failed to deactivate user. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setActivating(false);
    }
  };

  // Handle delete user
  const handleDelete = async () => {
    setDeleting(true);
    try {
      const result = await usersService.deleteUser(id);

      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'User deleted successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onDeleteClose();
        // Navigate back to users list
        navigate('/admin/users');
      } else {
        toast({
          title: 'Error',
          description: result.error || 'Failed to delete user',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error('Delete error:', err);
      toast({
        title: 'Error',
        description: 'Failed to delete user. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'purple';
      case 'center_admin':
        return 'blue';
      case 'blogger':
        return 'green';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Center minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Center>
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
        <Card p='40px' bg={cardBg}>
          <Flex direction='column' align='center' gap='20px'>
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              {error || 'User not found'}
            </Text>
            <Button onClick={() => navigate(-1)} leftIcon={<Icon as={MdArrowBack} />}>
              Go Back
            </Button>
          </Flex>
        </Card>
      </Box>
    );
  }

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Flex direction='column' gap='20px'>
        {/* Back, Edit, and Delete Buttons */}
        <Flex justify='space-between' align='center' mb='10px' flexWrap='wrap' gap='10px'>
          <Button
            onClick={() => navigate(-1)}
            leftIcon={<Icon as={MdArrowBack} />}
            variant='ghost'>
            Go Back
          </Button>
          <Flex gap='10px' flexWrap='wrap'>
            {user.is_active ? (
              <Button
                onClick={handleDeactivate}
                colorScheme='orange'
                variant='solid'
                isLoading={activating}
                loadingText="Deactivating">
                Deactivate
              </Button>
            ) : (
              <Button
                onClick={handleActivate}
                colorScheme='green'
                variant='solid'
                isLoading={activating}
                loadingText="Activating">
                Activate
              </Button>
            )}
            <Button
              onClick={onEditOpen}
              leftIcon={<Icon as={MdEdit} />}
              colorScheme='blue'
              variant='solid'>
              Edit User
            </Button>
            <Button
              onClick={onDeleteOpen}
              leftIcon={<Icon as={MdDelete} />}
              colorScheme='red'
              variant='solid'>
              Delete User
            </Button>
          </Flex>
        </Flex>

        {/* User Header */}
        <Card p='30px' bg={cardBg}>
          <Flex direction='column' gap='20px'>
            <Flex justify='space-between' align='center' flexWrap='wrap' gap='15px'>
              <Flex direction='column'>
                <Text color={textColor} fontSize='2xl' fontWeight='700' mb='5px'>
                  User Details
                </Text>
                <Text color={textColorSecondary} fontSize='sm'>
                  User ID: {user.id}
                </Text>
              </Flex>
              <Badge 
                colorScheme={getRoleBadgeColor(user.role)} 
                bg={bgStatus} 
                fontSize="md" 
                fontWeight="700" 
                px="15px" 
                py="5px"
              >
                {user.role ? user.role.replace('_', ' ').toUpperCase() : 'N/A'}
              </Badge>
            </Flex>
            <Divider borderColor={borderColor} />
          </Flex>
        </Card>

        {/* Personal Information */}
        <Card p='30px' bg={cardBg}>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdPerson} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Personal Information
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Full Name
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {user.name || 'N/A'}
              </Text>
            </Box>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdEmail} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Email
                </Text>
              </Flex>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {user.email || 'N/A'}
              </Text>
            </Box>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdLocationOn} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Center
                </Text>
              </Flex>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {user.center || 'N/A'}
              </Text>
            </Box>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdAccountCircle} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Role
                </Text>
              </Flex>
              <Badge
                colorScheme={getRoleBadgeColor(user.role)}
                bg={bgStatus}
                color={textColor}
                fontSize="sm"
                fontWeight="600"
                px="10px"
                py="4px"
                borderRadius="8px"
              >
                {user.role ? user.role.replace('_', ' ').toUpperCase() : 'N/A'}
              </Badge>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Account Status */}
        <Card p='30px' bg={cardBg}>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdAccountCircle} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Account Status
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Status
              </Text>
              <Badge
                colorScheme={user.is_active ? 'green' : 'red'}
                bg={bgStatus}
                color={textColor}
                fontSize="md"
                fontWeight="600"
                px="10px"
                py="4px"
                borderRadius="8px"
              >
                {user.is_active ? 'ACTIVE' : 'INACTIVE'}
              </Badge>
            </Box>
            <Box>
              <Flex align='center' gap='5px' mb='5px'>
                <Icon as={MdAccessTime} color={textColorSecondary} fontSize='sm' />
                <Text color={textColorSecondary} fontSize='sm'>
                  Last Access
                </Text>
              </Flex>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {user.last_access ? formatDate(user.last_access) : 'Never'}
              </Text>
            </Box>
          </SimpleGrid>
        </Card>

        {/* Timestamp Information */}
        <Card p='30px' bg={cardBg}>
          <Flex align='center' gap='10px' mb='20px'>
            <Icon as={MdCalendarToday} color={textColor} fontSize='xl' />
            <Text color={textColor} fontSize='xl' fontWeight='600'>
              Timestamp Information
            </Text>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing='20px'>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Account Creation
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {user.account_creation ? formatDate(user.account_creation) : 'N/A'}
              </Text>
            </Box>
            <Box>
              <Text color={textColorSecondary} fontSize='sm' mb='5px'>
                Created By
              </Text>
              <Text color={textColor} fontSize='md' fontWeight='600'>
                {user.created_by || 'System'}
              </Text>
            </Box>
          </SimpleGrid>
        </Card>
      </Flex>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} size="xl">
        <ModalOverlay />
        <ModalContent bg={cardBg}>
          <ModalHeader color={textColor}>Edit User</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap="20px">
              <FormControl>
                <FormLabel color={textColor}>Name</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter name"
                  bg={inputBg}
                  color={textColor}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>Email</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email"
                  bg={inputBg}
                  color={textColor}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>Center</FormLabel>
                <Input
                  value={formData.center}
                  onChange={(e) => setFormData({ ...formData, center: e.target.value })}
                  placeholder="Enter center"
                  bg={inputBg}
                  color={textColor}
                />
              </FormControl>
              <FormControl>
                <FormLabel color={textColor}>Role</FormLabel>
                <Select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  bg={inputBg}
                  color={textColor}
                >
                  <option value="admin">Admin</option>
                  <option value="center_admin">Center Admin</option>
                  <option value="blogger">Blogger</option>
                </Select>
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="is_active" mb="0" color={textColor}>
                  Active Status
                </FormLabel>
                <Switch
                  id="is_active"
                  isChecked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  colorScheme="green"
                />
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdate}
              isLoading={updating}
              loadingText="Updating">
              Update
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg={cardBg}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold" color={textColor}>
              Delete User
            </AlertDialogHeader>
            <AlertDialogBody color={textColor}>
              Are you sure you want to delete this user? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                isLoading={deleting}
                loadingText="Deleting">
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

